import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

import Footer from '../../components/common/Footer';
import AppSnackbar from '../../components/common/popups/AppSnackbar';
import Navbar from '../../components/common/Navbar';
import ParentStudentRegistrationModal from '../../components/parent/ParentStudentRegistrationModal';
import useParentAuth from '../../hooks/useParentAuth';
import useParentStudents from '../../hooks/useParentStudents';

const stats = [
  {
    title: 'Linked Students',
    icon: GroupsOutlinedIcon,
    iconClass: 'text-[#F6911D]',
    ringClass: 'border-[#F6911D]/35 bg-[#F6911D]/10',
  },
  {
    title: 'Active classes',
    value: 0,
    icon: CalendarMonthOutlinedIcon,
    iconClass: 'text-[#40d75f]',
    ringClass: 'border-[#40d75f]/35 bg-[#40d75f]/10',
  },
  {
    title: 'Pending Classes',
    value: 0,
    icon: AssignmentOutlinedIcon,
    iconClass: 'text-[#a46cff]',
    ringClass: 'border-[#a46cff]/35 bg-[#a46cff]/10',
  },
  {
    title: 'Unread Messages',
    value: 0,
    icon: MailOutlineOutlinedIcon,
    iconClass: 'text-[#56b6ff]',
    ringClass: 'border-[#56b6ff]/35 bg-[#56b6ff]/10',
    action: 'View messages',
  },
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const buildChildCard = (student, index) => ({
  id: student.id,
  initials:
    (student?.firstName?.[0] || student?.displayName?.[0] || 'S').toUpperCase() +
    ((student?.lastName?.[0] || student?.displayName?.split(' ')?.[1]?.[0] || '').toUpperCase()),
  circleClass: index % 2 === 0 ? 'bg-[#f28c18] border-[#f28c18] text-white' : 'bg-[#6f38c8] border-[#6f38c8] text-white',
  name: student?.displayName || 'Student',
  grade: student?.gradeLabel || 'Grade --',
  activeCount: toNumber(student?.classes?.activeCount ?? student?.classes?.active_count),
  pendingCount: toNumber(student?.classes?.pendingCount ?? student?.classes?.not_active_count),
  lastLogin: index % 2 === 0 ? 'Today, 9:15 AM' : 'Yesterday',
  dashboardAccent: 'border-[#f6911d] text-[#f6911d] hover:bg-[#f6911d]/8',
});

function StatCard({ item, value }) {
  const Icon = item.icon;

  return (
    <article className="rounded-[14px] border border-white/[0.08] bg-[#171717] px-5 py-5 shadow-[0_8px_22px_rgba(0,0,0,0.22)]">
      <div className={`flex h-12 w-12 items-center justify-center rounded-full border ${item.ringClass}`}>
        <Icon sx={{ fontSize: 24 }} className={item.iconClass} />
      </div>

      <h3 className="mt-4 text-sm font-medium text-white/70">{item.title}</h3>
      <div className="mt-1 text-[32px] font-semibold leading-none text-white">{value}</div>
      {item.action ? (
        <button type="button" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-white/65 hover:text-[#f6911d]">
          <span>{item.action}</span>
          <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </button>
      ) :''}

    </article>
  );
}

function ChildCard({ child, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="relative overflow-hidden rounded-[14px] border border-white/[0.08] bg-[#171717] px-5 py-5 text-left shadow-[0_10px_26px_rgba(0,0,0,0.26)]"
    >
      <div className="absolute right-4 top-4 text-white/70">
        <ArrowForwardIcon sx={{ fontSize: 22 }} />
      </div>

      <div className="flex items-center gap-4">
        <div
          className={`relative flex h-[74px] w-[74px] items-center justify-center rounded-full border text-[24px] font-semibold ${child.circleClass}`}
        >
          {child.initials}
          <span className="absolute bottom-0 right-0 h-4 w-4 rounded-full border-2 border-[#171717] bg-[#38d15a]" />
        </div>

        <div>
          <h3 className="text-[18px] font-semibold text-white">{child.name}</h3>
        </div>
      </div>

      <div className="mt-6 grid gap-4 border-t border-white/[0.08] pt-5 sm:grid-cols-3">
        <div>
          <div className="flex items-center gap-2 text-white/45">
            <AssignmentOutlinedIcon sx={{ fontSize: 18 }} />
            <span className="text-sm">Active classes</span>
          </div>
          <p className="mt-2 text-sm font-semibold text-[#46d764]">{child.activeCount}</p>
        </div>

        <div>
          <div className="flex items-center gap-2 text-white/45">
            <AssignmentOutlinedIcon sx={{ fontSize: 18 }} />
            <span className="text-sm">Pending classes</span>
          </div>
          <p className="mt-2 text-sm font-semibold text-[#a46cff]">{child.pendingCount}</p>
        </div>

        <div>
          <div className="flex items-center gap-2 text-white/45">
            <DashboardOutlinedIcon sx={{ fontSize: 18 }} />
            <span className="text-sm">Last Login</span>
          </div>
          <p className="mt-2 text-sm font-semibold text-white/85">{child.lastLogin}</p>
        </div>
      </div>

      <div
        className={`mt-5 inline-flex h-11 w-full items-center justify-center gap-3 rounded-[2px] border text-[15px] font-semibold transition-colors ${child.dashboardAccent}`}
      >
        <span>Classes</span>
        <ArrowForwardIcon sx={{ fontSize: 18 }} />
      </div>
    </button>
  );
}

const ParentLanding = () => {
  const navigate = useNavigate();
  const { user } = useParentAuth();
  const { students, loading, error, refresh } = useParentStudents();
  const [modalOpen, setModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const firstName = useMemo(
    () => user?.first_name || user?.name?.split(' ')?.[0] || user?.full_name?.split(' ')?.[0] || '',
    [user],
  );

  const children = useMemo(() => students.map((student, index) => buildChildCard(student, index)), [students]);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const openStudent = (student) => {
    navigate(`/parent/students/${student.id}`, { state: { student } });
  };

  const handleCreated = async () => {
    await refresh();
    setSnackbar({ open: true, message: 'Student registered successfully.', severity: 'success' });
  };

  return (
    <div className="min-h-screen bg-[#0b0b0b] text-white">
      <Navbar />

      <main className="px-4 py-5 sm:px-6 lg:px-8 lg:py-6">
        <section className="mx-auto max-w-[1214px]">
          <article className="overflow-hidden rounded-[16px] border border-white/[0.08] bg-[linear-gradient(135deg,#141414_0%,#101010_55%,#161008_100%)] px-6 py-6 shadow-[0_18px_60px_rgba(0,0,0,0.26)] sm:px-8 sm:py-8 lg:px-5 lg:py-5">
            <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
              <div className="max-w-[520px]">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#f6911d]">
                  Parent Portal
                </p>
                <h1 className="mt-4 text-[34px] font-semibold leading-[1.05] text-white sm:text-[44px] lg:text-[36px]">
                  Welcome back,
                  <br />
                  <span className="text-[#f6911d]">{firstName}</span>
                </h1>
                <p className="mt-5 max-w-[400px] text-[15px] leading-7 text-white/68 sm:text-[16px]">
                  Manage your children&apos;s learning, activities and progress from one place.
                </p>

                <button
                  type="button"
                  onClick={openModal}
                  className="mt-8 inline-flex h-[50px] items-center justify-center gap-3 rounded-md bg-[#ef8708] px-6 text-[15px] font-semibold text-white shadow-[0_12px_28px_rgba(239,135,8,0.2)] transition-opacity hover:opacity-95"
                >
                  <AddIcon sx={{ fontSize: 22 }} />
                  <span>Link New Student</span>
                </button>
              </div>

              <div className="relative min-h-[260px] lg:min-h-[330px]">
                <img className="max-w-[108%]" src="/assets/images/my_student.png" />
              </div>
            </div>
          </article>

          <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard item={stats[0]} value={loading ? '...' : String(students.length)} />
            <StatCard
              item={stats[1]}
              value={
                loading
                  ? '...'
                  : String(
                      students.reduce(
                        (sum, student) =>
                          sum +
                          toNumber(student?.classes?.activeCount ?? student?.classes?.active_count),
                        0,
                      ),
                    )
              }
            />
            <StatCard
              item={stats[2]}
              value={
                loading
                  ? '...'
                  : String(
                      students.reduce(
                        (sum, student) =>
                          sum +
                          toNumber(
                            student?.classes?.pendingCount ?? student?.classes?.not_active_count,
                          ),
                        0,
                      ),
                    )
              }
            />
            <StatCard item={stats[3]} value={stats[3].value} />
          </div>

          <div className="mt-8 flex items-center justify-between gap-4">
            <h2 className="text-[24px] font-semibold text-white">My Children</h2>
            <button
              type="button"
              onClick={() => navigate('/parent/students')}
              className="inline-flex items-center gap-2 text-[15px] font-medium text-[#f6911d] hover:text-[#ffb24f]"
            >
              <span>View all students</span>
              <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </button>
          </div>

          {error ? (
            <div className="mt-5 rounded-[14px] border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          ) : null}

          {loading ? (
            <div className="mt-5 rounded-[14px] border border-white/[0.08] bg-[#171717] px-5 py-10 text-center text-white/60">
              Loading students...
            </div>
          ) : children.length > 0 ? (
            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              {children.map((child, index) => (
                <ChildCard
                  key={child.id ?? child.name ?? index}
                  child={child}
                  onSelect={() => openStudent(child)}
                />
              ))}
            </div>
          ) : (
            <div className="mt-5 rounded-[14px] border border-white/[0.08] bg-[#171717] px-5 py-8 text-center">
              <p className="text-[16px] font-semibold text-white">
                No students have been added yet.
              </p>
              <p className="mt-2 text-sm text-white/60">
                Click &quot;Link New Student&quot; to register your first student.
              </p>
              <button
                type="button"
                onClick={openModal}
                className="mt-5 inline-flex items-center gap-2 rounded-md border border-[#f6911d] px-4 py-2 text-sm font-medium text-[#f6911d] hover:bg-[#f6911d]/8"
              >
                <AddIcon sx={{ fontSize: 18 }} />
                <span>Link New Student</span>
              </button>
            </div>
          )}

          <article className="mt-6 rounded-[16px] border border-white/[0.08] bg-[#171717] px-5 py-5 shadow-[0_10px_26px_rgba(0,0,0,0.22)] sm:px-6 sm:py-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#f6911d] text-[#f6911d]">
                  <ShieldOutlinedIcon sx={{ fontSize: 22 }} />
                </div>

                <div>
                  <h3 className="text-[18px] font-semibold text-white">
                    Your data is safe with us
                  </h3>
                  <p className="mt-1 max-w-[640px] text-[15px] leading-7 text-white/60">
                    We use industry-standard security to protect your children&apos;s information
                    and ensure a safe learning environment.
                  </p>
                </div>
              </div>
            </div>
          </article>
        </section>
        <Footer />
      </main>

      <ParentStudentRegistrationModal
        open={modalOpen}
        onClose={closeModal}
        onSuccess={handleCreated}
      />
      <AppSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </div>
  );
};

export default ParentLanding;
